import {ref} from 'vue';
import {Capacitor} from '@capacitor/core';
import {VoiceRecorder} from 'capacitor-voice-recorder';
import {AiApi} from '@app/api/admin/ai-api';
import {useAiAgentStore} from '@app/stores/ai-agent';
import { useAdminHomeStore } from '@app/stores';
import { useDrawer } from '@torim-app/shared-components';

const INPUT_SAMPLE_RATE = 16000;
const OUTPUT_SAMPLE_RATE = 24000;
const BUFFER_SIZE = 4096;

const isMuted = ref(false);
const isSpeaking = ref(false);
const currentVolume = ref(0);
const isConnected = ref(false);
const isMicOpen = ref(false);

let ws: WebSocket | null = null;
let isIntentionalClose = false;
let mediaStream: MediaStream | null = null;
let audioContext: AudioContext | null = null;
let scriptProcessor: ScriptProcessorNode | null = null;
let analyserNode: AnalyserNode | null = null;

let playbackContext: AudioContext | null = null;
let nextPlayTime = 0;
let playbackQueue: AudioBufferSourceNode[] = [];
let processingTimer: ReturnType<typeof setTimeout> | null = null;
const PROCESSING_TIMEOUT_MS = 30_000;

function resampleFloat32(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  const ratio = fromRate / toRate;
  const outputLength = Math.round(input.length / ratio);
  const output = new Float32Array(outputLength);
  for (let i = 0; i < outputLength; i++) {
    const srcIndex = i * ratio;
    const low = Math.floor(srcIndex);
    const high = Math.min(low + 1, input.length - 1);
    const frac = srcIndex - low;
    output[i] = input[low] * (1 - frac) + input[high] * frac;
  }
  return output;
}

function buildWsUrl(token: string): string {
  const backendUrl = `${import.meta.env.VITE_BACKEND_URL}`;
  const wsBase = backendUrl
    .replace(/^https:\/\//, 'wss://')
    .replace(/^http:\/\//, 'ws://');
  return `${wsBase}/ws/ai/live?token=${token}`;
}

export function useAiLive() {
  const store = useAiAgentStore();
  const adminMainStore = useAdminHomeStore();
  const { closeDrawer } = useDrawer();

  // ─── Connect to backend WebSocket ────────────────────────────────

  const connect = async (): Promise<void> => {
    const {token} = await AiApi.getLiveToken();
    const wsUrl = buildWsUrl(token);
    isIntentionalClose = false;

    return new Promise((resolve, reject) => {
      let resolved = false;
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        isConnected.value = true;
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (!resolved) {
            if (msg.type === 'ready') {
              resolved = true;
              resolve();
            } else if (msg.type === 'error') {
              resolved = true;
              reject(new Error(msg.message || 'AI session error'));
              return;
            }
          }
          handleServerMessage(msg);
        } catch (e) {
          console.error('[ai-live] Failed to parse WS message:', e);
        }
      };

      ws.onerror = (e) => {
        if (isIntentionalClose) {
          if (!resolved) { resolved = true; reject(new Error('intentional_close')); }
          return;
        }
        console.error('[ai-live] WebSocket error:', e);
        isConnected.value = false;
        if (!resolved) { resolved = true; reject(new Error('WebSocket connection failed')); }
      };

      ws.onclose = () => {
        isConnected.value = false;
        if (!resolved) { resolved = true; reject(new Error('WebSocket closed before ready')); }
        if (!isIntentionalClose) cleanupAudio();
      };

      setTimeout(() => {
        if (!resolved) { resolved = true; reject(new Error('Connection timeout')); }
      }, 20000);
    });
  };

  // ─── Audio playback context ────────────────────────────────────

  const ensurePlaybackContext = async () => {
    if (!playbackContext || playbackContext.state === 'closed') {
      playbackContext = new AudioContext({sampleRate: OUTPUT_SAMPLE_RATE});
      nextPlayTime = 0;
    }
    if (playbackContext.state === 'suspended') {
      await playbackContext.resume();
    }
  };

  // ─── Start session (connect + open mic) ─────────────────────────

  const startSession = async (): Promise<boolean> => {
    await ensurePlaybackContext();

    // Connect WS if needed
    if (!isConnected.value || !ws || ws.readyState !== WebSocket.OPEN) {
      try {
        store.setAgentState('processing');
        await connect();
      } catch (e: any) {
        if (e?.message === 'intentional_close') return false;
        console.error('[ai-live] Connection failed:', e);
        store.setError('לא הצלחתי להתחבר לשרת');
        store.setAgentState('idle');
        return false;
      }
    }

    // Open mic (always-on)
    if (!isMicOpen.value) {
      const micOk = await openMic();
      if (!micOk) return false;
    }

    store.setAgentState('listening');
    return true;
  };

  // ─── Open microphone (stays open until disconnect) ──────────────

  const openMic = async (): Promise<boolean> => {
    try {
      if (Capacitor.isNativePlatform()) {
        const permResult = await VoiceRecorder.requestAudioRecordingPermission();
        if (!permResult.value) {
          store.setError('אין הרשאת מיקרופון');
          store.setAgentState('idle');
          return false;
        }
      }

      mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: INPUT_SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      audioContext = new AudioContext();
      const actualRate = audioContext.sampleRate;
      console.log(`[ai-live] AudioContext sample rate: ${actualRate}, target: ${INPUT_SAMPLE_RATE}`);

      const source = audioContext.createMediaStreamSource(mediaStream);

      analyserNode = audioContext.createAnalyser();
      analyserNode.fftSize = 512;
      source.connect(analyserNode);

      scriptProcessor = audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);
      scriptProcessor.onaudioprocess = (e) => {
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        if (isMuted.value) return;

        const float32 = e.inputBuffer.getChannelData(0);

        const resampled = actualRate !== INPUT_SAMPLE_RATE
          ? resampleFloat32(float32, actualRate, INPUT_SAMPLE_RATE)
          : float32;

        const int16 = new Int16Array(resampled.length);
        for (let i = 0; i < resampled.length; i++) {
          const s = Math.max(-1, Math.min(1, resampled[i]));
          int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }

        const bytes = new Uint8Array(int16.buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        ws!.send(JSON.stringify({type: 'audio', data: btoa(binary)}));

        updateVolume();
      };

      source.connect(scriptProcessor);
      scriptProcessor.connect(audioContext.destination);

      isMicOpen.value = true;
      isMuted.value = false;
      return true;

    } catch (err: any) {
      console.error('[ai-live] Error opening mic:', err);
      store.setError(err?.name === 'NotAllowedError' ? 'אין הרשאת מיקרופון' : 'שגיאה בגישה למיקרופון');
      store.setAgentState('idle');
      return false;
    }
  };

  // ─── Mute / Unmute ─────────────────────────────────────────────

  const toggleMute = () => {
    isMuted.value = !isMuted.value;
    if (isMuted.value) {
      currentVolume.value = 0;
    }
  };

  // ─── Processing timeout ─────────────────────────────────────────

  const resetProcessingTimer = () => {
    if (processingTimer) {
      clearTimeout(processingTimer);
      processingTimer = null;
    }
  };

  const startProcessingTimer = () => {
    resetProcessingTimer();
    processingTimer = setTimeout(() => {
      if (store.getAgentState === 'processing') {
        store.setError('הבקשה לא הגיעה, נסו שוב');
        store.setAgentState('listening');
      }
    }, PROCESSING_TIMEOUT_MS);
  };

  // ─── Handle messages from backend ────────────────────────────────

  const handleServerMessage = (msg: any) => {
    switch (msg.type) {
      case 'audio':
        playAudioChunk(msg.data);
        if (store.getAgentState !== 'speaking') {
          resetProcessingTimer();
          store.finalizeUserMessage();
          store.setCurrentTool(null);
          store.setAgentState('speaking');
          isSpeaking.value = true;
        }
        break;

      case 'transcript':
        if (msg.role === 'user') {
          // Stream user transcription for live display
          store.setStreamingUserText(store.streamingUserText + msg.text);
        } else if (msg.role === 'assistant') {
          store.setStreamingResponse(store.streamingResponse + msg.text);
        }
        break;

      case 'corrected_input':
        store.addUserMessage(msg.text);
        break;

      case 'turn_complete': {
        isSpeaking.value = false;
        store.finalizeAssistantMessage();
        // Go back to listening (mic is always on)
        if (store.getAgentState === 'speaking') {
          const checkDone = () => {
            if (playbackQueue.length === 0) {
              store.setAgentState('listening');
            } else {
              setTimeout(checkDone, 200);
            }
          };
          setTimeout(checkDone, 300);
        }
        break;
      }

      case 'interrupted':
        stopPlayback();
        isSpeaking.value = false;
        store.finalizeAssistantMessage();
        store.setAgentState('listening');
        break;

      case 'state':
        store.setAgentState(msg.state);
        break;

      case 'tool_call':
        store.finalizeUserMessage();
        store.setAgentState('processing');
        store.setCurrentTool(msg.name);
        startProcessingTimer();
        break;

      case 'customer_card':
        store.addCustomerCard(msg.data);
        break;

      case 'calendar_changed':
        adminMainStore.fetchBusinessCalendarDataSilently();
        break;

      case 'error':
        store.setError(msg.message);
        break;

      case 'end_conversation':
        // AI decided the conversation is over — close the drawer
        closeDrawer();
        break;

      case 'session_closed':
        isConnected.value = false;
        break;
    }
  };

  // ─── Volume monitoring ───────────────────────────────────────────

  const updateVolume = () => {
    if (!analyserNode) return;
    const dataArray = new Uint8Array(analyserNode.fftSize);
    analyserNode.getByteTimeDomainData(dataArray);
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const val = (dataArray[i] - 128) / 128;
      sum += val * val;
    }
    currentVolume.value = Math.min(1, Math.sqrt(sum / dataArray.length) * 5);
  };

  // ─── Audio playback ──────────────────────────────────────────────

  const playAudioChunk = (base64Data: string) => {
    ensurePlaybackContext();

    const binary = atob(base64Data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const int16 = new Int16Array(bytes.buffer);

    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768;

    const buffer = playbackContext.createBuffer(1, float32.length, OUTPUT_SAMPLE_RATE);
    buffer.getChannelData(0).set(float32);

    const source = playbackContext.createBufferSource();
    source.buffer = buffer;
    source.connect(playbackContext.destination);

    const now = playbackContext.currentTime;
    const startTime = Math.max(now + 0.01, nextPlayTime);
    source.start(startTime);
    nextPlayTime = startTime + buffer.duration;

    playbackQueue.push(source);
    source.onended = () => {
      const idx = playbackQueue.indexOf(source);
      if (idx !== -1) playbackQueue.splice(idx, 1);
    };
  };

  const stopPlayback = () => {
    for (const s of playbackQueue) { try { s.stop(); } catch {} }
    playbackQueue = [];
    nextPlayTime = 0;
    isSpeaking.value = false;
  };

  // ─── Send text message ───────────────────────────────────────────

  const sendTextMessage = async (message: string) => {
    if (!message.trim()) return;
    if (!isConnected.value) {
      if (!(await startSession())) return;
    }

    store.addUserMessage(message);
    store.setAgentState('processing');
    ws!.send(JSON.stringify({type: 'text', text: message}));
  };

  // ─── Stop TTS (interrupt AI) ────────────────────────────────────

  const stopTts = () => {
    stopPlayback();
    store.finalizeAssistantMessage();
    store.setAgentState('listening');
  };

  // ─── Disconnect (only on drawer close) ───────────────────────────

  const disconnect = () => {
    resetProcessingTimer();
    cleanupMic();
    stopPlayback();
    if (ws) {
      isIntentionalClose = true;
      ws.close();
      ws = null;
    }
    isConnected.value = false;
    isMicOpen.value = false;
    isMuted.value = false;
    currentVolume.value = 0;
  };

  // ─── Cleanup helpers ─────────────────────────────────────────────

  const cleanupMic = () => {
    if (scriptProcessor) { scriptProcessor.disconnect(); scriptProcessor = null; }
    if (analyserNode) { analyserNode.disconnect(); analyserNode = null; }
    if (audioContext) { audioContext.close().catch(() => {}); audioContext = null; }
    if (mediaStream) { mediaStream.getTracks().forEach(t => t.stop()); mediaStream = null; }
    isMicOpen.value = false;
  };

  const cleanupAudio = () => {
    cleanupMic();
    stopPlayback();
    if (playbackContext) { playbackContext.close().catch(() => {}); playbackContext = null; }
  };

  return {
    isMuted,
    isSpeaking,
    currentVolume,
    isConnected,
    isMicOpen,
    startSession,
    toggleMute,
    stopTts,
    sendTextMessage,
    disconnect,
  };
}
